// src/screens/ClubTeamCreate.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Shield,
  Check,
  AlertTriangle,
  Loader2,
  Users,
  ArrowRight,
} from "lucide-react";

type ClubRole = "dirigeant" | "cadre_technique";

function memberRoleToUiRole(dbRole: string): ClubRole | null {
  switch (dbRole) {
    case "dirigeant":
    case "admin":
    case "manager":
      return "dirigeant";
    case "cadre_technique":
    case "assistant":
      return "cadre_technique";
    default:
      return null;
  }
}

export default function ClubTeamCreate() {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [canAccess, setCanAccess] = useState(false);

  const [name, setName] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function checkAccess() {
      if (!clubId) {
        setErrorMsg("Club introuvable.");
        setLoading(false);
        return;
      }

      try {
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError) throw authError;
        if (!user) throw new Error("Utilisateur non connecté.");

        const { data, error } = await supabase
          .from("club_members")
          .select("role")
          .eq("club_id", clubId)
          .eq("user_id", user.id)
          .eq("status", "active");

        if (error) throw error;

        const roles = (data || [])
          .map((row) => memberRoleToUiRole(row.role))
          .filter(Boolean) as ClubRole[];

        if (!cancelled) {
          setCanAccess(roles.length > 0);
        }
      } catch (err: any) {
        if (!cancelled) {
          setErrorMsg(err?.message || "Impossible de vérifier tes accès.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, [clubId]);

  async function createTeam(e: React.FormEvent) {
    e.preventDefault();
    if (!clubId || !canAccess) return;

    const cleanName = name.trim();
    if (!cleanName) return;

    setSaving(true);
    setErrorMsg(null);

    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) throw authError;
      if (!user) throw new Error("Utilisateur non connecté.");

      const payload = {
        club_id: clubId,
        team_name: cleanName,
        league: category.trim() || null,
        user_id: user.id,
        created_by: user.id,
      };

      const { data: newTeam, error } = await supabase
        .from("teams")
        .insert(payload)
        .select("id")
        .single();

      if (error) throw error;

      navigate(`/club/${clubId}/team/${newTeam.id}/members`);
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible de créer l’équipe.");
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          Vérification des accès…
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <div>
              <div className="font-semibold">Accès refusé</div>
              <div className="text-sm mt-1">
                Cette page est réservée aux administrateurs, dirigeants et cadres techniques du club.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center sm:text-left">
        <div className="text-sm text-gray-500 dark:text-gray-400">Organisation</div>
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center justify-center gap-2 sm:justify-start">
          <Shield className="h-5 w-5 text-gray-500" />
          Créer une équipe
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Une fois l’équipe créée, tu seras redirigé vers sa page membres pour inviter et lier joueurs et parents.
        </div>
      </div>

      {errorMsg && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
          {errorMsg}
        </div>
      )}

      <div className="max-w-lg rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <form onSubmit={createTeam} className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Nom</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: U18 Région, Seniors Filles RF2…"
              className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
              required
              disabled={saving}
            />
          </div>

          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">
              Catégorie (optionnel)
            </label>
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="RF2, U18, U15…"
              className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Link
              to={`/club/${clubId}/teams`}
              className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Annuler
            </Link>
            <button
              type="submit"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm"
              disabled={saving || !name.trim()}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {saving ? "Création…" : "Créer et lier joueurs/parents"}
            </button>
          </div>
        </form>
      </div>

      <div className="max-w-lg rounded-2xl border border-gray-200 dark:border-gray-800 p-4 text-sm text-gray-600 dark:text-gray-300">
        <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
          <Users className="h-4 w-4 text-gray-500" />
          Et ensuite ?
        </div>
        <p className="mt-2">
          Après la création, tu arrives sur la page membres de l’équipe : tu pourras y inviter des
          joueurs et des parents, puis lier chaque parent à son ou ses enfant(s).
        </p>
        <Link
          to={`/club/${clubId}/teams`}
          className="mt-3 inline-flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100 hover:underline"
        >
          Retour à la liste des équipes <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
